<template>
  <div v-if="command">
    <div class="row" style="text-align: left;">
      <div class="col"></div>
      <div class="col text-end">
        <u v-if="showMyIp" @click="useMyIp" class="clickable me-2">My IP</u>
        <u v-if="showResolve" @click="resolve" class="clickable">Resolve domain</u>
        <u v-if="resolvingLoading" class="clickable me-2">
          <div class="spinner-border" role="status" style="width: 0.9rem; height: 0.9rem;"></div>
        </u>
      </div>
    </div>
    <input v-if="!command.rules.includes('none')" id="user_input" type="text"
      class="form-control form-control-lg monospaced fa" v-model="inputValue" :placeholder="placeholder"
      @keydown.enter="submit">
    <br>
    <button type="button" class="btn btn-lg btn-primary form-control monospaced" @click="submit"
      :disabled="!isValid || loading">
      <div v-if="loading" class="spinner-border" role="status" style="width: 1rem; height: 1rem;"></div>
      <span v-else><i class="fa-regular fa-hand-pointer"></i> Execute</span>
    </button>
  </div>
</template>

<script>
export default {
  name: 'CommandInput',
  props: {
    command: {
      type: Object,
      required: true
    },
    clientIp: String,
    dnsResolver: String,
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      inputValue: '',
      isValid: false,
      resolvingLoading: false
    }
  },
  computed: {
    showMyIp() {
      return this.command.rules.includes('ipv4') && this.clientIp
    },
    showResolve() {
      return (this.command.rules.includes('domain') || this.command.rules.includes('ipv4')) &&
        this.validateDomain(this.inputValue) &&
        !this.resolvingLoading &&
        !this.validateIPv4(this.inputValue) &&
        !this.validateIPv6(this.inputValue)
    },
    placeholder() {
      return this.command.rules.map(rule => this.ruleToName(rule)).join(' / ')
    }
  },
  watch: {
    inputValue() {
      this.validateInput()
    },
    command() {
      this.inputValue = ''
      this.validateInput()
    }
  },
  mounted() {
    this.validateInput()
  },
  methods: {
    useMyIp() {
      this.inputValue = this.clientIp
    },
    async resolve() {
      if (!this.dnsResolver) return

      this.resolvingLoading = true
      try {
        const response = await fetch(this.dnsResolver + `/dns-query?name=${this.inputValue}&type=A`, {
          headers: { 'Accept': 'application/dns-json' }
        })
        const data = await response.json()

        if (data.Answer?.length > 0) {
          this.inputValue = data.Answer[0].data
        }
      } catch (error) {
        console.error('DNS resolution failed:', error)
      } finally {
        this.resolvingLoading = false
      }
    },
    submit() {
      if (this.isValid && !this.loading) {
        this.$emit('execute', this.inputValue)
      } else {
        const inputEl = document.getElementById('user_input')
        if (inputEl) {
          inputEl.classList.add('invalid')
          inputEl.focus()
          inputEl.select()
          setTimeout(() => inputEl.classList.remove('invalid'), 2000)
        }
      }
    },
    validateInput() {
      this.isValid = this.command.rules.some(rule => this.validateRule(rule))
    },
    validateRule(rule) {
      switch (rule) {
        case 'ipv4': return this.validateIPv4(this.inputValue)
        case 'ipv6': return this.validateIPv6(this.inputValue)
        case 'domain': return this.validateDomain(this.inputValue)
        case 'cidr': return this.validateCidr(this.inputValue)
        case 'asn': return this.validateASN(this.inputValue)
        case 'mac': return this.validateMAC(this.inputValue)
        case 'none': return true
        default: return true
      }
    },
    validateIPv4(ip) {
      const ipRegex = /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
      return ipRegex.test(ip)
    },
    validateIPv6(ip) {
      const ipRegex = /^(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|2[0-4][0-9]|[1-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[1-9][0-9]?)|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|2[0-4][0-9]|[1-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[1-9][0-9]?))$/
      return ipRegex.test(ip)
    },
    validateCidr(cidr) {
      if (!cidr) return false
      const [ip, prefix] = cidr.split('/')
      if (!prefix) return false
      const prefixNum = parseInt(prefix)
      if (isNaN(prefixNum)) return false
      if (this.validateIPv4(ip)) {
        return prefixNum >= 0 && prefixNum <= 32
      } else if (this.validateIPv6(ip)) {
        return prefixNum >= 0 && prefixNum <= 128
      }
      return false
    },
    validateDomain(domain) {
      const domainRegex = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/
      return domainRegex.test(domain)
    },
    validateASN(asn) {
      if (!asn) return false;

      // Проверка на формат ASXXXX (например, AS12345)
      const asnRegexWithPrefix = /^AS(\d{1,5})$/i;
      if (asnRegexWithPrefix.test(asn)) {
        const num = parseInt(asn.replace('AS', ''), 10);
        return num >= 1 && num <= 4294967295;
      }

      // Проверка на простое число, но только если строка не содержит точек
      if (/\./.test(asn)) return false;

      // Преобразование в число и проверка диапазона
      const asnNumber = parseInt(asn, 10);
      return !isNaN(asnNumber) && asnNumber >= 1 && asnNumber <= 4294967295;
    },
    validateMAC(mac) {
      if (!mac) return false
      const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/
      return macRegex.test(mac)
    },
    ruleToName(rule) {
      const names = {
        ipv4: 'IPv4',
        ipv6: 'IPv6',
        domain: 'Domain',
        mac: 'MAC address',
        asn: 'ASN',
        cidr: 'CIDR',
        none: 'Input not required',
        any: 'Any'
      }
      return names[rule] || 'Input'
    }
  }
}
</script>

<style scoped>
.invalid {
  animation: shake .5s linear;
}

.invalid:focus {
  border-color: #FF0000 !important;
  box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 8px rgba(255, 0, 0, 0.6) !important;
}

@keyframes shake {

  8%,
  41% {
    transform: translateX(-10px);
  }

  25%,
  58% {
    transform: translateX(10px);
  }

  75% {
    transform: translateX(-5px);
  }

  92% {
    transform: translateX(5px);
  }

  0%,
  100% {
    transform: translateX(0);
  }
}
</style>